import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  Grid,
  Paper,
  Avatar,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { fetchReport, handleError, formatDate2 } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import axios from 'axios';

const UsersJoinRequestReport = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const snackbar = useSnackbar();
  const { setNavigation, resetNavigation, state } = useNavigationHelperStore();
  
  // Dialog state
  const [dialogState, setDialogState] = useState({
    open: false,
    joinRequest: null,
    action: null  // 'approve' or 'reject'
  });
  
  // Load saved search parameters when returning to the page
  useEffect(() => {
    if (state) {
      const { page: savedPage, data: savedData } = state;
      
      if (savedPage) setPage(savedPage);
      if (savedData) {
        setData(savedData);
      }

      resetNavigation();
      
      // Always fetch fresh data even if we have saved state
      fetchJoinRequests(savedPage || 1);
    } else {
      // Initial load
      fetchJoinRequests(1);
    }
  }, []);

  // Save search parameters when leaving this page
  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      page,
      data
    });
  };

  const fetchJoinRequests = async (pageNumber) => {
    setIsLoading(true);
    try {
      const response = await fetchReport('USER_JOIN_REQUEST', pageNumber);
      
      setData(response);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchJoinRequests(value);
  };

  const handleActionClick = (joinRequest, action) => {
    setDialogState({
      open: true,
      joinRequest,
      action
    });
  };

  const handleCloseDialog = () => {
    setDialogState({
      open: false,
      joinRequest: null,
      action: null
    });
  };

  const handleConfirmAction = async () => {
    const { joinRequest, action } = dialogState;
    if (!joinRequest || !action) return;

    setIsLoading(true);
    try {
      let response;
      if (action === 'approve') {
        const endpoint = `${import.meta.env.VITE_API_URL}users_requests?accept_request`;
        response = await axios.post(
          endpoint,
          { id: joinRequest.id },
          { withCredentials: true }
        );
      } else if (action === 'reject') {
        const endpoint = `${import.meta.env.VITE_API_URL}users_requests?id=${joinRequest.id}`;
        response = await axios.delete(
          endpoint,
          {
            withCredentials: true 
          }
        );
      }

      // Refresh the data
      await fetchJoinRequests(page);
      
      snackbar(action === 'approve' ? t('reports.requestApproved') : t('reports.requestRejected'));
      handleCloseDialog();
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const getDialogContent = () => {
    const { joinRequest, action } = dialogState;
    if (!joinRequest) return null;
    const userForDisplay = joinRequest.user;

    if (action === 'approve') {
      return (
        <>
          <DialogTitle>{t('reports.approveJoinRequest')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('reports.confirmApprove', { name: userForDisplay.name || userForDisplay.email })}
            </DialogContentText>
          </DialogContent>
        </>
      );
    } else {
      return (
        <>
          <DialogTitle>{t('reports.rejectJoinRequest')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('reports.confirmReject', { name: userForDisplay.name || userForDisplay.email })}
            </DialogContentText>
          </DialogContent>
        </>
      );
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {data ? (
        <>
          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <GroupAddIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">
                {t('reports.joinRequestsTitle')} ({data.total_results || (data.content ? data.content.length : 0)})
              </Typography>
            </Box>
          </Box>
          
          {data.content && data.content.length > 0 ? (
            <>
              {data.content.map((joinRequest) => (
                <Paper 
                  key={joinRequest.id} 
                  elevation={2}
                  sx={{ 
                    p: 2, 
                    mb: 2, 
                    '&:hover': { boxShadow: 6 } // Enhanced hover effect
                  }}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    {/* Column 1: Avatar */}
                    <Grid item xs={12} sm="auto">
                      <Avatar
                        src={joinRequest.user?.image_url}
                        alt={joinRequest.user?.name}
                        sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}
                      >
                        {joinRequest.user?.name ? joinRequest.user.name.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                    </Grid>

                    {/* Column 2: User Info & Comment */}
                    <Grid item xs={12} sm>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {joinRequest.user?.name || t('reports.noName')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {joinRequest.user?.email}
                      </Typography>
                      {joinRequest.user?.phone && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {t('reports.telPrefix')} {joinRequest.user.phone}
                        </Typography>
                      )}
                    </Grid>

                    {/* Column 3: Date & Actions */}
                    <Grid item xs={12} sm="auto" sx={{ textAlign: { xs: 'left', sm: 'right' }, mt: { xs: 2, sm: 0 } }}>
                      <Box display="flex" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                        <Tooltip title={t('reports.approve')}>
                          <IconButton
                            size="medium"
                            sx={{ mr: 0.5 }}
                            onClick={() => handleActionClick(joinRequest, 'approve')}
                          >
                            <CheckCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('reports.reject')}>
                          <IconButton
                            size="medium"
                            onClick={() => handleActionClick(joinRequest, 'reject')}
                          >
                            <CancelOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={3} mb={2}> {/* Added mb */}
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3} component={Paper} variant="outlined">
              <GroupAddIcon color="action" sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} /> {/* Larger icon */}
              <Typography variant="h6" color="text.secondary"> {/* More prominent text */}
                {t('reports.noJoinRequests')}
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('common.noData')}</Typography>
        </Box>
      )}
      
      {/* Confirmation Dialog */}
      <Dialog
        open={dialogState.open}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        {getDialogContent()}
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleConfirmAction} 
            variant="contained" 
            color={dialogState.action === 'approve' ? 'success' : 'error'}
            autoFocus
          >
            {dialogState.action === 'approve' ? t('reports.approve') : t('reports.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersJoinRequestReport; 